import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useOrgSnapshot } from "../hooks/useOrgSnapshot";
import { Auth, Orgs, Profile } from "../lib/apiTyped";
import { useApiCall } from "../lib/useToastError";
import { useNotifications } from "../components/shared/NotificationsProvider";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/shared/Card";
import { Button } from "../components/shared/Button";
import { FormInput } from "../components/shared/FormInput";
import { useTour } from "../components/tour/useTour";

const LOGO_ACCEPT = "image/png,image/jpeg,image/webp";
const LOGO_MAX_BYTES = 1_500_000;

// Self-serve account settings. Open to every signed-in user, regardless
// of role (admin / owner / member / external). Three GDPR-aligned blocks:
//   - Profile: edit your display name; email is read-only because it is
//     tied to the OAuth/magic-link identity.
//   - Data portability (Art. 20): one-click JSON download of every record
//     about you.
//   - Right to erasure (Art. 17): delete your account. Backend refuses if
//     you are still owner of an active org so we don't strand it.
//
// Sign-out lives in the sidebar already — not duplicated here.

export function SettingsPage() {
  const { user, profile, signOut, impersonation } = useAuth();
  const navigate = useNavigate();
  const { run } = useApiCall();
  const { confirm, toast } = useNotifications();
  const tour = useTour();
  // Solo el owner real de la org puede editar el logo. Usamos el snapshot
  // (no las claims) porque es la fuente de verdad del rol actual y refleja
  // cambios sin necesidad de refrescar el token.
  const { orgId, org, role } = useOrgSnapshot();
  const isOrgOwner = role === "owner";
  const orgLogoUrl = (org as { logoUrl?: string | null } | null)?.logoUrl ?? null;
  const orgName = (org as { name?: string } | null)?.name ?? "";

  const [name, setName] = useState(profile?.display_name ?? "");
  const [savingName, setSavingName] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoDeleting, setLogoDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function saveName() {
    const v = name.trim();
    if (!v) {
      toast("El nombre no puede estar vacío.", "error");
      return;
    }
    setSavingName(true);
    await run(() => Profile.updateDisplayName(v), {
      successMessage: "Nombre actualizado.",
    });
    setSavingName(false);
  }

  async function exportData() {
    setExporting(true);
    const data = await run(() => Auth.exportData());
    setExporting(false);
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `formular-data-export-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function onLogoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reseteamos el input antes de validar para que volver a elegir el
    // mismo archivo dispare onChange (sin esto, un re-upload tras error
    // silencioso queda muerto).
    e.target.value = "";
    if (!file || !orgId) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      toast("Formato no soportado. Usa PNG, JPEG o WebP.", "error");
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast(
        `El logo no puede superar ${Math.round(LOGO_MAX_BYTES / 1024)} KB.`,
        "error",
      );
      return;
    }
    setLogoUploading(true);
    await run(() => Orgs.uploadLogo(orgId, file), {
      successMessage: "Logo actualizado.",
    });
    setLogoUploading(false);
  }

  async function deleteLogo() {
    if (!orgId) return;
    const ok = await confirm({
      title: "¿Borrar el logo?",
      message: "Volverá a mostrarse el nombre por defecto en toda la app.",
      confirmLabel: "Borrar",
      variant: "danger",
    });
    if (!ok) return;
    setLogoDeleting(true);
    await run(() => Orgs.deleteLogo(orgId), {
      successMessage: "Logo eliminado.",
    });
    setLogoDeleting(false);
  }

  async function deleteAccount() {
    const ok1 = await confirm({
      title: "¿Borrar tu cuenta?",
      message:
        "Esta acción es irreversible. Se eliminará tu perfil, se revocarán tus accesos a las organizaciones donde eres miembro y se borrará tu identidad de Firebase. Si eres owner de alguna org activa, primero deberás transferir la propiedad o cancelar el plan.",
      confirmLabel: "Continuar",
      variant: "danger",
    });
    if (!ok1) return;
    const ok2 = await confirm({
      title: "Confirmación final",
      message:
        "Última oportunidad. Pulsa Borrar para eliminar tu cuenta de forma permanente.",
      confirmLabel: "Borrar",
      variant: "danger",
    });
    if (!ok2) return;
    setDeleting(true);
    const result = await run(() => Auth.deleteAccount());
    setDeleting(false);
    if (!result) return;
    // Force a clean signout — the Auth user is already gone server-side.
    try {
      await signOut();
    } catch {
      /* ignore */
    }
    navigate("/", { replace: true });
  }

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Ajustes"
        subtitle="Gestiona tu perfil, tus datos personales y el cierre de tu cuenta."
      />

      <div className="space-y-6">
        <Card className="p-6">
          <h2 className="text-base font-semibold text-ink mb-4">Perfil</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold mb-1 text-ink-3">
                Email
              </label>
              <div className="text-sm text-ink-2 break-all">
                {user?.email ?? "—"}
              </div>
              <p className="text-xs text-ink-3 mt-1">
                Vinculado a tu identidad de inicio de sesión y no se puede
                cambiar desde aquí.
              </p>
            </div>
            <FormInput
              label="Nombre"
              value={name}
              onChange={setName}
              placeholder="Tu nombre"
            />
            <Button
              variant="primary"
              onClick={saveName}
              disabled={savingName || name.trim() === (profile?.display_name ?? "")}
            >
              {savingName ? "Guardando…" : "Guardar nombre"}
            </Button>
          </div>
        </Card>

        {isOrgOwner && orgId && (
          <Card className="p-6">
            <h2 className="text-base font-semibold text-ink mb-1">
              Logo de la organización
            </h2>
            <p className="text-sm text-ink-3 mb-4">
              Aparece en la esquina superior de la app para ti y tu equipo.
              PNG, JPEG o WebP hasta {Math.round(LOGO_MAX_BYTES / 1024)} KB.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div
                className="w-48 aspect-[3/1] rounded-2xl bg-surface-2 border border-border overflow-hidden shrink-0 flex items-center justify-center"
                aria-label="Vista previa del logo"
              >
                {orgLogoUrl ? (
                  <img
                    src={orgLogoUrl}
                    alt={orgName || "Logo"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-xs font-semibold text-ink-3 uppercase">
                    {orgName.slice(0, 2) || "—"}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={LOGO_ACCEPT}
                  className="hidden"
                  onChange={onLogoFile}
                />
                <Button
                  variant="primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={logoUploading || logoDeleting}
                >
                  {logoUploading
                    ? "Subiendo…"
                    : orgLogoUrl
                      ? "Cambiar logo"
                      : "Subir logo"}
                </Button>
                {orgLogoUrl && (
                  <Button
                    variant="ghost"
                    onClick={deleteLogo}
                    disabled={logoUploading || logoDeleting}
                  >
                    {logoDeleting ? "Borrando…" : "Borrar"}
                  </Button>
                )}
              </div>
            </div>
          </Card>
        )}

        {!impersonation && (
          <Card className="p-6">
            <h2 className="text-base font-semibold text-ink mb-1">
              Tour de la app
            </h2>
            <p className="text-sm text-ink-3 mb-4">
              ¿Te perdiste el recorrido inicial? Lánzalo de nuevo para repasar
              dónde está cada cosa.
            </p>
            <Button variant="ghost" onClick={() => tour.start()}>
              Volver a ver el tour
            </Button>
          </Card>
        )}

        <Card className="p-6">
          <h2 className="text-base font-semibold text-ink mb-2">
            Tus datos (RGPD)
          </h2>
          <p className="text-sm text-ink-2 mb-4">
            Derecho a la portabilidad (art. 20 RGPD). Descarga un fichero
            JSON con todo lo que guardamos sobre ti: tu perfil, tus
            membresías y un resumen de actividad reciente.
          </p>
          <Button variant="ghost" onClick={exportData} disabled={exporting}>
            {exporting ? "Preparando…" : "Descargar mis datos"}
          </Button>
        </Card>

        <Card className="p-6 border" style={{ borderColor: "#C61418" }}>
          <h2
            className="text-base font-semibold mb-2"
            style={{ color: "#C61418" }}
          >
            Borrar cuenta
          </h2>
          <p className="text-sm text-ink-2 mb-4">
            Derecho de supresión (art. 17 RGPD). Borra tu perfil, revoca
            todas tus membresías y elimina tu identidad. Si eres owner de
            alguna organización activa, la operación se rechazará — primero
            transfiere la propiedad o cancela el plan.
          </p>
          <Button
            variant="danger"
            onClick={deleteAccount}
            disabled={deleting}
          >
            {deleting ? "Borrando…" : "Borrar mi cuenta permanentemente"}
          </Button>
        </Card>
      </div>
    </div>
  );
}
